/**
 * The document head for the tool pages.
 *
 * ── The two preloads are the whole "no waiting" promise ──────────────────────
 *
 * `useFaceEngine` starts the download on mount, which is already after React
 * has hydrated. These two `<link rel="preload">` tags start it from the HTML
 * parser instead — the browser has the WASM and the model in flight while it is
 * still building the DOM, which on a cold connection is the difference between
 * a warm scanner and a visible wait. They are the reason the tool pages do not
 * show a progress bar in the common case.
 *
 * `as: "fetch"` with `crossOrigin: "anonymous"` for both, and both are needed:
 * without the crossOrigin the preload lands in a different cache partition than
 * the `fetch()` MediaPipe makes, and the browser downloads all 16 MB twice —
 * which is worse than not preloading at all and shows up in the network panel
 * as two identical requests nobody can explain.
 *
 * ── Why SoftwareApplication and not WebApplication ───────────────────────────
 *
 * Google's own documentation treats `SoftwareApplication` as the type with rich
 * result support and `WebApplication` as a subtype it reads less consistently.
 * Both are honest here — the tool genuinely is software that runs in a browser —
 * so the one more likely to be read wins.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import { MODEL_URL, WASM_BASE } from "../face/engine";
import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { toolPath, toolsIndexPath } from "./index";
import type { ToolCopy, ToolSlug } from "./types";
import type { ToolsUi } from "./ui";

/**
 * The engine as structured data.
 *
 * `offers` states zero rather than being omitted: "free" is the single most
 * important fact about these pages for anybody choosing between them and a
 * competitor, and an absent price is read as unknown rather than as free.
 */
function toolJsonLd(copy: ToolCopy, path: string, lang: ContentLang) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: copy.h1,
    description: copy.description,
    url: canonical(path),
    applicationCategory: "UtilitiesApplication",
    // The browser IS the operating system here. Saying "iOS, Android" would be
    // describing the app rather than this page, and the distinction is the
    // whole pitch: this one needs no install.
    operatingSystem: "Any (runs in a web browser)",
    browserRequirements: "Requires JavaScript and WebAssembly",
    inLanguage: lang,
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      category: "free",
    },
    publisher: { "@type": "Organization", name: "AKES Solutions Limited" },
  };
}

function enginePreloads() {
  return [
    {
      rel: "preload",
      as: "fetch",
      href: MODEL_URL,
      crossOrigin: "anonymous",
    },
    {
      rel: "preload",
      as: "fetch",
      href: `${WASM_BASE}/vision_wasm_internal.wasm`,
      crossOrigin: "anonymous",
    },
  ];
}

/** Head for one tool page. */
export function toolHead(
  slug: ToolSlug,
  lang: ContentLang,
  copy: ToolCopy,
  ui: ToolsUi,
) {
  const path = toolPath(slug, lang);
  return {
    meta: [
      { title: copy.title },
      { name: "description", content: copy.description },
      ...socialMeta({
        title: copy.title,
        description: copy.description,
        path,
        lang,
        imageAlt: copy.h1,
      }),
      { "script:ld+json": toolJsonLd(copy, path, lang) },
      // Every question in this block is rendered visibly on the page, which
      // Google requires and which is enforced by both being generated from
      // `copy.faq`.
      { "script:ld+json": faqJsonLd([...copy.faq]) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path: toolsIndexPath(lang) },
          { name: copy.h1, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates((l) => toolPath(slug, l)),
      ...enginePreloads(),
    ],
  };
}

/** Head for the tools index. */
export function toolsIndexHead(
  lang: ContentLang,
  ui: ToolsUi,
  items: { name: string; path: string }[],
) {
  const path = toolsIndexPath(lang);
  return {
    meta: [
      { title: ui.indexTitle },
      { name: "description", content: ui.indexDescription },
      ...socialMeta({
        title: ui.indexTitle,
        description: ui.indexDescription,
        path,
        lang,
        imageAlt: ui.indexH1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: ui.indexH1,
          description: ui.indexDescription,
          url: canonical(path),
          inLanguage: lang,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: items.length,
            itemListElement: items.map((item, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: item.name,
              url: canonical(item.path),
            })),
          },
        },
      },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path },
        ]),
      },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(toolsIndexPath),
      // The index preloads the engine too: it is one tap from every tool, and
      // warming it here means the first tool someone opens is already ready.
      ...enginePreloads(),
    ],
  };
}
